import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

/** Chiave della preferenza in `localStorage`: l'unico dato che B.Site salva nel browser. */
export const THEME_STORAGE_KEY = 'bsite-theme';

/**
 * Tema chiaro e scuro (FR-029, research R-19). La scelta è `light`, `dark` o `system`; senza
 * scelta si segue il sistema operativo. Il risultato è `data-theme` su `<html>`, che attiva la
 * variante `dark` di bsite-theme.css. Lo script inline di index.html fa lo stesso calcolo prima
 * del primo disegno, così la pagina non lampeggia.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly media =
    this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)') ?? null;
  private readonly systemDark = signal(this.media?.matches ?? false);

  readonly preference = signal<ThemePreference>(this.readStored());
  readonly theme = computed<Theme>(() => {
    const preference = this.preference();
    if (preference === 'system') {
      return this.systemDark() ? 'dark' : 'light';
    }
    return preference;
  });

  constructor() {
    this.media?.addEventListener?.('change', (event: MediaQueryListEvent) => {
      this.systemDark.set(event.matches);
      this.apply();
    });
    this.apply();
  }

  set(preference: ThemePreference): void {
    this.preference.set(preference);
    try {
      this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Memoria del browser non disponibile (navigazione privata, dati bloccati): il tema vale
      // comunque per questa pagina.
    }
    this.apply();
  }

  private apply(): void {
    this.document.documentElement.setAttribute('data-theme', this.theme());
  }

  private readStored(): ThemePreference {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
    } catch {
      return 'system';
    }
  }
}
